import React, { useEffect, useState } from 'react';
import {
  Card,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Button,
  Empty,
  Tooltip,
  Modal,
  InputNumber,
  Collapse
} from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  WalletOutlined,
  UserOutlined,
  FileOutlined,
  DownloadOutlined,
  DeleteOutlined,
  UsergroupAddOutlined,
  ArrowLeftOutlined,
  CrownOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { TournamentService } from '@/services/tournament.service';
import { TournamentFileService } from '@/services/tournament-file.service';
import type { Tournament } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { EditTournamentModal } from './EditTournamentModal';
import { formatDate } from '@/utils/date';
import { RegisterParticipantsModal } from './RegisterParticipantsModal';
import { useAuth } from '@/contexts/AuthContext';
import { OrganizerDisplay } from './OrganizerDisplay';
import { getUploadsBaseUrl, normalizeUploadImageUrl } from '@/config/api.config';
import {
  StyledCard,
  InfoBlock,
  StyledDivider,
  FeesCard,
  FeeBlock
} from './styles/TournamentInfoContent.styles';

const { Title, Paragraph } = Typography;

interface TournamentInfoContentProps {
  tournament: Tournament;
  activeTab?: string;
  onEdit?: () => void;
}

export const TournamentInfoContent: React.FC<TournamentInfoContentProps> = ({ tournament, activeTab, onEdit }) => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  
  // Используем внешний onEdit если он передан, иначе используем локальное состояние
  // const handleEditClick = onEdit || (() => setIsEditModalVisible(true));
  const [isRegisterModalVisible, setIsRegisterModalVisible] = useState(false);
  const [actualParticipantCount, setActualParticipantCount] = useState<number | null>(null);
  const [isGenerateBracketsModalVisible, setIsGenerateBracketsModalVisible] = useState(false);
  const [generatingBrackets, setGeneratingBrackets] = useState(false);
  const [numTatami, setNumTatami] = useState<number>(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Функция для возврата на список турниров с сохранением фильтров
  const handleBack = () => {
    // Используем activeTab из props, затем из state, затем по умолчанию 'upcoming'
    const savedTab = activeTab || location.state?.activeTab || 'upcoming';
    navigate('/tournaments', { 
      state: { activeTab: savedTab },
      replace: false 
    });
  };

  // Логируем coverImage для отладки
  useEffect(() => {
    if (tournament?.id) {
      // const coverImageUrl = tournament.coverImage 
      //   ? tournament.coverImage.startsWith('http') 
      //     ? tournament.coverImage.replace('/api/uploads', '/uploads')
      //     : (() => {
      //         const getBaseUrl = () => {
      //           const envUrl = import.meta.env.VITE_API_URL;
      //           if (envUrl) {
      //             return envUrl.replace(/\/api\/?$/, '');
      //           }
      //           const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV;
      //           return isDev ? 'http://localhost:3001' : '';
      //         };
      //         const baseUrl = getBaseUrl();
      //         let imagePath = tournament.coverImage.startsWith('/') ? tournament.coverImage : '/' + tournament.coverImage;
      //         imagePath = imagePath.replace(/^\/api\//, '/');
      //         return `${baseUrl}${imagePath}`;
      //       })()
      //   : undefined;
      
      // Removed verbose logging for production
    }
  }, [tournament]);

  useEffect(() => {
    if (tournament?.id) {
      TournamentService.getParticipantCount(tournament.id)
        .then(count => setActualParticipantCount(count))
        .catch(err => logger.error('Error fetching participant count:', err));
    }
  }, [tournament?.id]);

  const handleEditSuccess = async () => {
    setIsEditModalVisible(false);
    message.success('Турнир успешно обновлен');
    // TODO: при необходимости здесь можно добавить локальное обновление данных турнира через проп onEdit или refetch.
  };

  const handleFileDownload = async (fileId: number, fileName: string) => {
    try {
      await TournamentFileService.download(fileId, fileName);
    } catch (error) {
      logger.error('Error downloading file:', error);
      message.error('Ошибка при скачивании файла');
    }
  };

  const handleFileDelete = async (fileId: number) => {
    try {
      await TournamentFileService.delete(fileId);
      message.success('Файл успешно удален');
      // Локально удаляем файл из списка без перезагрузки страницы
      if (tournament?.files) {
        (tournament as any).files = tournament.files.filter((f: any) => f.id !== fileId);
      }
    } catch (error) {
      logger.error('Error deleting file:', error);
      message.error('Ошибка при удалении файла');
    }
  };

  const handleRegisterSuccess = async () => {
    setIsRegisterModalVisible(false);
    if (tournament?.id) {
      try {
        const count = await TournamentService.getParticipantCount(tournament.id);
        setActualParticipantCount(count);
      } catch (error) {
        logger.error('Error updating participant count:', error);
      }
    }
    message.success('Участники успешно зарегистрированы');
  };

  const handleGenerateBrackets = async () => {
    if (!tournament?.id) return;

    console.group('🚀 [TournamentInfoContent] Начало генерации пулов');
    console.log('📋 Данные турнира:', {
      id: tournament.id,
      name: tournament.name,
      status: tournament.status,
      numTatami
    });

    try {
      setGeneratingBrackets(true);
      const result = await TournamentService.generateBrackets(tournament.id, numTatami);
      console.log('✅ Результат генерации:', result);
      message.success('Пулы успешно сформированы');
      setIsGenerateBracketsModalVisible(false);
      console.groupEnd();
      // Страница не перезагружается; сетки будут видны на вкладке "Сетки"/"Жеребьевка".
    } catch (error: any) {
      console.error('❌ [TournamentInfoContent] Ошибка при генерации пулов:', error);
      console.error('Детали ошибки:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      console.groupEnd();
      logger.error('Error generating brackets:', error);
      message.error(error?.response?.data?.message || error?.message || 'Ошибка при формировании пулов');
    } finally {
      setGeneratingBrackets(false);
    }
  };

  const registrationButton = () => {
    if (!tournament) return null;

    if (!tournament.tournamentCategories?.length) {
      return (
        <Tooltip title="Регистрация будет доступна после добавления категорий">
          <Button type="primary" disabled>
            Регистрация закрыта
          </Button>
        </Tooltip>
      );
    }

    if (new Date(tournament.registrationDeadline) <= new Date()) {
      return (
        <Tooltip title="Срок регистрации истёк">
          <Button type="primary" disabled>
            Регистрация закрыта
          </Button>
        </Tooltip>
      );
    }

    if (tournament.status !== 'published') {
      return (
        <Tooltip title="Турнир ещё не опубликован">
          <Button type="primary" disabled>
            Регистрация закрыта
          </Button>
        </Tooltip>
      );
    }

    if (!user || !['admin', 'manager', 'participant'].includes(user.role)) {
      return (
        <Tooltip title="Для регистрации необходимо авторизоваться">
          <Button type="primary" disabled>
            Регистрация недоступна
          </Button>
        </Tooltip>
      );
    }

    if (['admin', 'manager'].includes(user.role)) {
      return (
        <Button
          type="primary"
          onClick={() => setIsRegisterModalVisible(true)}
          icon={<UsergroupAddOutlined />}
        >
          Регистрация участников
        </Button>
      );
    }
  };


  if (!tournament) {
    return <Empty description="Турнир не найден" />;
  }

  const coverImageUrl = normalizeUploadImageUrl(
    tournament.coverImage
      ? tournament.coverImage.startsWith('http')
        ? tournament.coverImage
        : (() => {
            const baseUrl = getUploadsBaseUrl();
            const imagePath = tournament.coverImage!.startsWith('/') ? tournament.coverImage! : '/' + tournament.coverImage;
            return `${baseUrl}${imagePath}`;
          })()
      : undefined
  );

  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <Button 
          type="default" 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          style={{ marginBottom: '16px' }}
        >
          Назад к списку турниров
        </Button>
      </div>
      <StyledCard>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={16}>
            {/* Отображаем coverImage по центру над полем "Дата проведения" */}
            {coverImageUrl && (
              <div style={{ 
                marginBottom: '24px', 
                borderRadius: '8px', 
                overflow: 'hidden', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '120px',
                background: '#f0f0f0',
                position: 'relative'
              }}>
                <img
                  src={coverImageUrl}
                  alt={tournament.name}
                  style={{ 
                    width: '100%', 
                    maxHeight: '400px', 
                    objectFit: 'contain', 
                    display: 'block' 
                  }}
                  onError={(e) => {
                    logger.error('Error loading cover image:', {
                      url: coverImageUrl,
                      tournamentId: tournament.id
                    });
                    const el = e.target as HTMLImageElement;
                    el.style.display = 'none';
                    const placeholder = el.parentElement?.querySelector('.cover-image-placeholder');
                    if (placeholder) (placeholder as HTMLElement).style.display = 'flex';
                  }}
                />
                <div
                  className="cover-image-placeholder"
                  style={{
                    display: 'none',
                    position: 'absolute',
                    inset: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '14px'
                  }}
                >
                  Изображение недоступно
                </div>
              </div>
            )}
            
            <InfoBlock>
              <CalendarOutlined />
              <span>Дата проведения: {formatDate(tournament.tournamentDate)}</span>
            </InfoBlock>

            <InfoBlock>
              <EnvironmentOutlined />
              <span>Место проведения: {tournament.location}</span>
            </InfoBlock>

            <InfoBlock>
              <ClockCircleOutlined />
              <span>Окончание регистрации: {formatDate(tournament.registrationDeadline)}</span>
            </InfoBlock>

            <InfoBlock>
              <CrownOutlined />
              <span>
                Организатор: <OrganizerDisplay 
                  organizerId={tournament.organizerId} 
                  clubId={tournament.clubId}
                  organizerName={tournament.organizer?.name}
                  clubName={tournament.club?.name}
                />
              </span>
            </InfoBlock>

            {tournament.description && (
              <>
                <StyledDivider />
                <InfoBlock>
                  <InfoCircleOutlined />
                  <Paragraph style={{ margin: 0 }}>{tournament.description}</Paragraph>
                </InfoBlock>
              </>
            )}
          </Col>

          <Col xs={24} md={8}>
            <Title level={5}>Документы</Title>
            <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
              {tournament.files && tournament.files.length > 0 ? (() => {
                const documents = tournament.files.filter(file => {
                  const fileType = file.fileType || file.mimeType || '';
                  return !fileType.startsWith('image/');
                });
                return documents.length > 0 ? (
                  documents.map((file) => (
                    <div
                      key={file.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }}
                    >
                      <FileOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      <span style={{ flex: 1 }}>{file.originalName || file.fileName}</span>
                      <Space>
                        <Button
                          type="text"
                          icon={<DownloadOutlined />}
                          size="small"
                          onClick={() => handleFileDownload(file.id, file.originalName || file.fileName)}
                        />
                        {(user?.role === 'admin' ||
                          user?.id === tournament.createdById ||
                          user?.id === tournament.organizerId ||
                          (user?.role === 'manager' && tournament.clubId && user.clubId === tournament.clubId) ||
                          (user?.role === 'manager' && tournament.clubId && user.clubId === tournament.clubId)) && (
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => handleFileDelete(file.id)}
                          />
                        )}
                      </Space>
                    </div>
                  ))
                ) : (
                  <Empty
                    description="Нет документов"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ margin: '16px 0' }}
                  />
                );
              })() : (
                <Empty
                  description="Нет документов"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '16px 0' }}
                />
              )}
            </Space>

            <Card>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Участников"
                    value={actualParticipantCount !== null ? actualParticipantCount : (tournament.participantsCount || 0)}
                    prefix={<TeamOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Категорий"
                    value={tournament.tournamentCategories?.length || 0}
                    prefix={<TrophyOutlined />}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: '16px' }}>
                {(() => {
                  const button = registrationButton();
                  if (!button) return null;
                  return React.cloneElement(button as React.ReactElement, {
                    block: true,
                    style: {
                      backgroundColor: '#722ed1',
                      borderColor: '#722ed1',
                      height: '40px',
                      fontSize: '14px',
                      width: '100%',
                    }
                  });
                })()}
              </div>
            </Card>

            {user && (
              <FeesCard title={<><WalletOutlined /> Взносы за участие</>}>
                <FeeBlock>
                  <UserOutlined className="fee-icon" />
                  <span>Члены клуба:</span>
                  <span className="fee-amount">{tournament.feeForClubMembers} ₽</span>
                </FeeBlock>
                <FeeBlock>
                  <TeamOutlined className="fee-icon" />
                  <span>Остальные участники:</span>
                  <span className="fee-amount">{tournament.feeForOthers} ₽</span>
                </FeeBlock>
              </FeesCard>
            )}

            {tournament.beltLevels && tournament.beltLevels.length > 0 && (
              <>
                <StyledDivider />
                <Title level={5}>Категории</Title>
                <Space wrap>
                  {tournament.beltLevelsLocalized?.map((belt) => (
                    <Tag key={belt.value} color="blue">
                      {belt.label}
                    </Tag>
                  ))}
                </Space>
              </>
            )}

            {tournament.tournamentCategories && tournament.tournamentCategories.length > 0 && (
              <>
                <StyledDivider />
                <Collapse
                  items={[
                    {
                      key: 'categories',
                      label: <Title level={5} style={{ margin: 0 }}>Категории</Title>,
                      children: (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {(() => {
                            const seen = new Set<number>();
                            const unique = tournament.tournamentCategories.filter((tc) => {
                              const key = tc.weightCategory?.id ?? tc.id;
                              if (seen.has(key)) return false;
                              seen.add(key);
                              return true;
                            });
                            return [...unique]
                              .sort((a, b) => {
                                const wA = a.weightCategory;
                                const wB = b.weightCategory;
                                const ageA = wA?.ageFrom ?? 0;
                                const ageB = wB?.ageFrom ?? 0;
                                if (ageA !== ageB) return ageA - ageB;
                                const weightA = wA?.weightFrom ?? 0;
                                const weightB = wB?.weightFrom ?? 0;
                                if (weightA !== weightB) return weightA - weightB;
                                const genderOrder: Record<string, number> = { female: 1, male: 2, mixed: 3 };
                                const genderA = genderOrder[wA?.gender ?? 'mixed'] ?? 3;
                                const genderB = genderOrder[wB?.gender ?? 'mixed'] ?? 3;
                                if (genderA !== genderB) return genderA - genderB;
                                return (a.id ?? 0) - (b.id ?? 0);
                              })
                              .map((tc) => (
                            <div
                              key={tc.id}
                              style={{
                                padding: '8px',
                                background: '#f5f5f5',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            >
                              {tc.weightCategory.name}
                            </div>
                          ));
                          })()}
                        </Space>
                      )
                    }
                  ]}
                  ghost
                />
              </>
            )}

            {tournament.streamLinks && Object.keys(tournament.streamLinks).length > 0 && (
              <>
                <StyledDivider />
                <Title level={5}>
                  <VideoCameraOutlined style={{ marginRight: 8 }} />
                  Трансляции
                </Title>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {Object.entries(tournament.streamLinks)
                    .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
                    .map(([tatami, url]) => (
                      <div
                        key={tatami}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          border: '1px solid #f0f0f0',
                          borderRadius: '4px',
                          marginBottom: '4px'
                        }}
                      >
                        <span style={{ marginRight: 8, fontWeight: 500, minWidth: 70 }}>Татами {tatami}</span>
                        <Button type="link" href={url} target="_blank" rel="noopener noreferrer" style={{ padding: 0 }}>
                          {url.length > 50 ? `${url.slice(0, 47)}...` : url}
                        </Button>
                      </div>
                    ))}
                </Space>
              </>
            )}
          </Col>
        </Row>
      </StyledCard>
      {!onEdit && (
        <EditTournamentModal
          visible={isEditModalVisible}
          tournament={tournament}
          onCancel={() => setIsEditModalVisible(false)}
          onSuccess={handleEditSuccess}
        />
      )}
      <RegisterParticipantsModal
        visible={isRegisterModalVisible}
        tournament={tournament}
        onCancel={() => setIsRegisterModalVisible(false)}
        onSuccess={handleRegisterSuccess}
      />
      <Modal
        title="Сформировать пулы"
        open={isGenerateBracketsModalVisible}
        onOk={handleGenerateBrackets}
        onCancel={() => setIsGenerateBracketsModalVisible(false)}
        confirmLoading={generatingBrackets}
        okText="Сформировать"
        cancelText="Отмена"
      >
        <p>Вы уверены, что хотите сформировать пулы для этого турнира?</p>
        <p>Это действие распределит всех зарегистрированных участников по пулам в соответствии с их категориями.</p>

        <p>При формировании пулов будут учтены следующие условия:</p>
        <ul>
          <li>Максимально развести бойцов от одного тренера в разные ветки</li>
          <li>Максимально развести бойцов с одного клуба</li>
          <li>Максимально развести бойцов с одного города</li>
          <li>Максимально развести бойцов с одной федерации</li>
        </ul>

        <div style={{ marginTop: '16px' }}>
          <Typography.Text strong>Укажите количество татами:</Typography.Text>
          <InputNumber
            min={1}
            max={10}
            value={numTatami}
            onChange={(value) => setNumTatami(value || 1)}
            style={{ marginLeft: '8px', width: '60px' }}
          />
        </div>
      </Modal>
    </>
  );
};

export default TournamentInfoContent;


